// Minimal promise-based IndexedDB wrapper for offline support (no deps).
// Two object stores: "queue" (offline-queued submissions, keyPath "id") and
// "cache" (offline read-through response cache, keyPath "key").
// IndexedDB is used over localStorage because queued clock-outs can carry
// base64 signature images and the cache holds per-client reference data —
// localStorage's ~5MB quota is too tight for that.

const DB_NAME = "oppo_offline";
const DB_VERSION = 1;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("queue")) {
        db.createObjectStore("queue", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("cache")) {
        db.createObjectStore("cache", { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

function tx(storeName, mode, operation) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        const request = operation(store);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }),
  );
}

export function dbGet(storeName, key) {
  return tx(storeName, "readonly", (s) => s.get(key));
}

export function dbGetAll(storeName) {
  return tx(storeName, "readonly", (s) => s.getAll());
}

export function dbPut(storeName, value) {
  return tx(storeName, "readwrite", (s) => s.put(value));
}

export function dbDelete(storeName, key) {
  return tx(storeName, "readwrite", (s) => s.delete(key));
}

export function dbClear(storeName) {
  return tx(storeName, "readwrite", (s) => s.clear());
}

/** True once openDb has succeeded at least once this session. */
export function dbAvailable() {
  return openDb().then(
    () => true,
    () => false,
  );
}
